import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Row, Col, Alert } from 'react-bootstrap';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Spinner from 'react-bootstrap/Spinner';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { AccountContext } from '../Accounts';
import { DataTypeContext } from '../DataTypes';
import { API } from '../Constants';
import { useCustomTranslator } from '../utils/customTranslator';

const Dashboard = () => {
    const { clientId, firstName, lastName, logout, updateFirstAndLastName, isAccountDisabled, setIsAccountDisabled } = useContext(AccountContext);
    const { dataTypes, isDeliveryEnabled } = useContext(DataTypeContext);
    const canBookAppointment = dataTypes.agencies.length > 0 && dataTypes?.appointmentOptions?.enableAppointmentBooking;
    const hasAppointment = dataTypes.agencies.length > 0;
    const { t } = useTranslation();
    const customT = useCustomTranslator();
    const history = useHistory();
    const location = useLocation();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [isLoading, setIsLoading] = useState(false);

    const isLoginRedirect = location?.state?.isLoginRedirect === true;

    const updateClientName = useCallback(async () => {
        setIsLoading(true);
        try {
            const token = await executeRecaptcha('details');
            const res = await fetch(`${API.CLIENT_DETAILS_URL}/${clientId}?token=${token}`);
            if (!res.ok) {
                throw new Error(res.status);
            }
            const clientData = await res.json();

            if (clientData && clientData.clientProfileId) {
                if (clientData.status === 'deceased' || clientData.status === 'archived' || clientData.status === 'anonymized') {
                    setIsAccountDisabled(true);
                } else {
                    updateFirstAndLastName(clientData.firstName, clientData.lastName);
                }
            }
        } catch (error) {
            setIsAccountDisabled(true);
        } finally {
            setIsLoading(false);
        }
    }, [clientId, executeRecaptcha, updateFirstAndLastName, setIsAccountDisabled]);

    useEffect(() => {
        if (isLoginRedirect) updateClientName();
    }, [isLoginRedirect, updateClientName]);

    const actions = [];

    if (canBookAppointment && isDeliveryEnabled) {
        actions.push(
            {
                to: '/book-delivery',
                title: t('dashboard.bookDelivery'),
                description: t('dashboard.bookDeliveryDescription'),
            },
            {
                to: '/book-appointment',
                title: t('dashboard.bookPickup'),
                description: t('dashboard.bookPickupDescription'),
            }
        );
    } else if (canBookAppointment) {
        actions.push({
            to: '/book-appointment',
            title: t('dashboard.bookServices'),
            description: t('dashboard.bookServicesDescription'),
        });
    }

    if (hasAppointment) {
        actions.push({
            to: '/upcoming-appointments',
            title: t('dashboard.upcomingServices'),
            description: t('dashboard.upcomingServicesDescription'),
        });
    }

    actions.push(
        {
            to: '/update-user',
            title: t('dashboard.updateInfo'),
            description: t('dashboard.updateInfoDescription'),
        },
        {
            to: { pathname: '/intake', state: { edit: true } },
            title: t('dashboard.updateProfile'),
            description: t('dashboard.updateProfileDescription'),
        }
    );

    const handleLogout = () => {
        logout();
        history.push('/');
    };

    if (isLoginRedirect && isLoading) {
        return (
            <Col lg={6} md={6} sm={12} className="mx-auto">
                <div className="text-center mt-4">
                    <Spinner animation="border" variant="primary" />
                </div>
            </Col>
        );
    }

    if (isAccountDisabled) {
        return (
            <Col lg={6} md={6} sm={12} className="mx-auto">
                <br />
                <Alert variant="danger">
                    <Alert.Heading>{t('account.disabled')}</Alert.Heading>
                    <hr />
                    <p>{customT('api.error.ACCOUNT_DISABLED')}</p>
                </Alert>
            </Col>
        );
    }

    return (
        <Col lg={6} md={6} sm={12} className="mx-auto">
            <div className="card shadow-sm border-0 mb-3">
                <div className="card-body p-4 p-md-5">
                    <h1 className="h3 mb-2">
                        {t('core.welcome')}, {firstName} {lastName}
                    </h1>
                    <p className="h6 text-muted mb-4">
                        {t('core.clientId')}: {clientId}
                    </p>

                    <div>
                        {actions.map((action, index) => (
                            <Link
                                key={`${index}-${action.title}`}
                                to={action.to}
                                className={`dashboard-action-link d-flex align-items-center justify-content-between py-3 px-3 rounded border bg-white text-dark text-decoration-none ${index < actions.length - 1 ? 'mb-3' : ''}`}
                            >
                                <div>
                                    <div className="h5 mb-1">{action.title}</div>
                                    <small className="dashboard-action-description text-muted">{action.description}</small>
                                </div>
                                <span className="dashboard-action-arrow text-muted h4 mb-0" aria-hidden="true">&rarr;</span>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>

            <div className="text-center">
                <button type="button" className="btn btn-link text-primary" onClick={handleLogout}>
                    {t('core.action.logout')}
                </button>
            </div>
        </Col>
    );
};

export default Dashboard;
